import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { aggregatedClasses } from '@elliemae/ds-classnames';
import TabsPanels from './TabsPanels.js';
import TabBar from './TabBar.js';
import SubTabBar from './SubTabBar.js';
import { TabTypes } from './TabTypes.js';
import '@babel/runtime/helpers/esm/classCallCheck';
import '@babel/runtime/helpers/esm/createClass';
import '@babel/runtime/helpers/esm/assertThisInitialized';
import '@babel/runtime/helpers/esm/inherits';
import '@babel/runtime/helpers/esm/possibleConstructorReturn';
import '@babel/runtime/helpers/esm/getPrototypeOf';
import 'memoize-one';
import 'react-swipeable-views';
import './PanelContainer.js';
import '@babel/runtime/helpers/esm/objectWithoutProperties';
import 'lodash';
import '@elliemae/ds-form/RequiredMark';
import 'resize-observer';
import 'styled-components';
import './Carousel.js';
import '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import '@elliemae/ds-icons/ChevronRight';
import '@elliemae/ds-icons/ChevronLeft';
import '@elliemae/ds-button';
import './useResize.js';

var _TabBarComponentsByTy;
var blockName = 'tabs';
var StyledTabsContainer = aggregatedClasses('div')(blockName);
var StyledTabBarContainer = aggregatedClasses('div')(blockName, 'tab-bar-container');
var TabBarComponentsByType = (_TabBarComponentsByTy = {}, _defineProperty(_TabBarComponentsByTy, TabTypes.NORMAL, TabBar), _defineProperty(_TabBarComponentsByTy, TabTypes.SUBTABS, SubTabBar), _TabBarComponentsByTy);

var TabsImpl = function TabsImpl(props) {
  var _tabs$, _tabs$$props;

  var animated = props.animated,
      tabs = props.children,
      type = props.type,
      tabBarExtraContent = props.tabBarExtraContent,
      onTabChange = props.onTabChange,
      enableMouseEvents = props.enableMouseEvents,
      innerRef = props.innerRef,
      onlyRenderActiveTab = props.onlyRenderActiveTab,
      containerProps = props.containerProps,
      allowTextSelection = props.allowTextSelection,
      tabsListAriaLabel = props.tabsListAriaLabel,
      activeTab = props.activeTab,
      firstSubtabRef = props.firstSubtabRef,
      lastTabRef = props.lastTabRef,
      withCarousel = props.withCarousel,
      flexibleTabsHeaders = props.flexibleTabsHeaders,
      fixedTabsHeaders = props.fixedTabsHeaders,
      isDSMobile = props.isDSMobile;

  var _useState = useState(activeTab || (tabs === null || tabs === void 0 ? void 0 : (_tabs$ = tabs[0]) === null || _tabs$ === void 0 ? void 0 : (_tabs$$props = _tabs$.props) === null || _tabs$$props === void 0 ? void 0 : _tabs$$props.tabId)),
      _useState2 = _slicedToArray(_useState, 2),
      actualActiveTab = _useState2[0],
      setActualActiveTab = _useState2[1];

  var firstTabRefByParent = useRef(null);
  var lastTabRefByParent = useRef(null);
  useEffect(function () {
    if (activeTab) {
      setActualActiveTab(activeTab);
    }
  }, [activeTab]);
  var handleTabClick = useCallback(function (tab) {
    setActualActiveTab(tab);
    onTabChange(tab);
  }, [onTabChange]);
  var handleDownArrow = useCallback(function (e) {
    var _firstTabRefByParent$;

    e.preventDefault();
    e.stopPropagation();
    if (firstTabRefByParent !== null && firstTabRefByParent !== void 0 && firstTabRefByParent.current) (_firstTabRefByParent$ = firstTabRefByParent.current) === null || _firstTabRefByParent$ === void 0 ? void 0 : _firstTabRefByParent$.focus();
  }, [firstTabRefByParent]);
  var handleUpArrow = useCallback(function (e) {
    var _lastTabRef$current;

    e.preventDefault();
    e.stopPropagation();
    if (lastTabRef !== null && lastTabRef !== void 0 && lastTabRef.current) (_lastTabRef$current = lastTabRef.current) === null || _lastTabRef$current === void 0 ? void 0 : _lastTabRef$current.focus();
  }, [lastTabRef]);
  var PureTabBarComponent = useMemo(function () {
    var TabBarComponent = TabBarComponentsByType[type] || TabBar;
    return /*#__PURE__*/React.createElement(TabBarComponent, {
      withCarousel: withCarousel,
      activeTab: actualActiveTab,
      onTabClick: handleTabClick,
      tabs: tabs,
      firstSubtabRef: firstSubtabRef,
      lastTabRef: lastTabRefByParent,
      handleDownArrow: handleDownArrow,
      handleUpArrow: handleUpArrow,
      isDSMobile: isDSMobile,
      flexibleTabsHeaders: flexibleTabsHeaders,
      fixedTabsHeaders: fixedTabsHeaders
    });
  }, [type, withCarousel, actualActiveTab, handleTabClick, tabs, firstSubtabRef, lastTabRefByParent, handleDownArrow, handleUpArrow, isDSMobile]);
  var PureTabsPanels = useMemo(function () {
    return /*#__PURE__*/React.createElement(TabsPanels, {
      activeTab: actualActiveTab,
      allowTextSelection: allowTextSelection,
      animated: animated,
      enableMouseEvents: enableMouseEvents,
      onlyRenderActiveTab: onlyRenderActiveTab,
      onTabChange: handleTabClick,
      tabsComponents: tabs,
      lastTabRef: lastTabRefByParent,
      firstTabRefByParent: firstTabRefByParent
    });
  }, [actualActiveTab, allowTextSelection, animated, enableMouseEvents, onlyRenderActiveTab, handleTabClick, tabs, lastTabRefByParent, firstTabRefByParent]);
  var PureTabsImpl = useMemo(function () {
    return /*#__PURE__*/React.createElement(StyledTabsContainer, _extends({}, containerProps, {
      "aria-label": tabsListAriaLabel,
      innerRef: innerRef,
      role: "tablist"
    }), /*#__PURE__*/React.createElement(StyledTabBarContainer, null, PureTabBarComponent, tabBarExtraContent), PureTabsPanels);
  }, [containerProps, tabsListAriaLabel, innerRef, PureTabBarComponent, tabBarExtraContent, PureTabsPanels]);
  return PureTabsImpl;
};

TabsImpl.propTypes = {
  animated: PropTypes.bool,
  enableMouseEvents: PropTypes.bool,
  allowTextSelection: PropTypes.bool,
  onTabChange: PropTypes.func,
  type: PropTypes.oneOf(['normal', 'subtabs']),
  tabBarExtraContent: PropTypes.element,
  onlyRenderActiveTab: PropTypes.bool,
  withCarousel: PropTypes.bool,
  activeTab: PropTypes.string,
  children: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.any
  })).isRequired,
  tabsListAriaLabel: PropTypes.string,
  containerProps: PropTypes.object,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  firstSubtabRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  lastTabRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),

  /** Whether or not it's a ds-tab from DSMobile tabs */
  isDSMobile: PropTypes.bool,

  /** DSMobile only flexible tabs mode */
  flexibleTabsHeaders: PropTypes.bool,

  /** DSMobile only fixed tabs mode */
  fixedTabsHeaders: PropTypes.bool
};

export default TabsImpl;
//# sourceMappingURL=TabsImpl.js.map
